import { useEffect, useRef } from "react";
import tracking, { EventTypes } from "../lib/tracking";
import { useAppConfig } from "./useAppConfig";
import useConfiguration from "./useConfiguration";
import useCalculation from "./useCalculation";
import useMoney from "./useMoney";

export const useTracking = () => {
  const { title, bgColor, color, link, currentDeal, currentSupplier, kwh } =
    useAppConfig();

  const { suppliers } = useConfiguration();
  const { totalCompare, totalCurrent, difference } = useCalculation();
  const { addVat } = useMoney();
  const previousIsCheaperRef = useRef<boolean | null>(null);

  // Track deal changes
  useEffect(() => {
    if (currentDeal) {
      tracking()?.track(EventTypes.DEAL_CHANGED, {
        deal: currentDeal.name,
        supplier: currentDeal.provider.name,
      });
    }
  }, [currentDeal]);

  // Track supplier changes
  useEffect(() => {
    if (currentSupplier && suppliers) {
      tracking()?.track(EventTypes.SUPPLIER_CHANGED, {
        supplier: suppliers.find((supplier) => supplier.id === currentSupplier)
          ?.name,
        deal: currentDeal?.name,
      });
    }
  }, [currentSupplier, suppliers, currentDeal?.name]);

  // Track kWh changes
  useEffect(() => {
    if (kwh) {
      tracking()?.track(EventTypes.KWH_CHANGED, {
        kwh: kwh,
      });
    }
  }, [kwh]);

  // Track difference changes (only when totalCompare changes)
  useEffect(() => {
    if (currentDeal && totalCompare !== undefined) {
      tracking()?.track(EventTypes.DIFFERENCE_CHANGED, {
        supplier: currentDeal?.provider.name,
        deal: currentDeal?.name,
        difference_dkk_pr_year: difference
          ? parseInt(difference.toString())
          : 0,
        current_dkk_pr_year: totalCurrent ? addVat(totalCurrent) : 0,
        compare_dkk_pr_year: addVat(totalCompare),
      });
    }
  }, [totalCompare, currentDeal]);

  useEffect(() => {
    if (!currentDeal) return;

    const isCurrentlyCheaper = totalCurrent > totalCompare;
    const previousIsCheaper = previousIsCheaperRef.current;

    // Only track if the state has changed
    if (
      previousIsCheaper !== null &&
      previousIsCheaper !== isCurrentlyCheaper
    ) {
      if (isCurrentlyCheaper) {
        tracking()?.track(EventTypes.IS_CHEAPER, {
          supplier: currentDeal?.provider.name,
          deal: currentDeal?.name,
          difference_dkk_pr_year: difference
            ? parseInt(difference.toString())
            : 0,
          current_dkk_pr_year: totalCurrent ? addVat(totalCurrent) : 0,
          compare_dkk_pr_year: addVat(totalCompare),
        });
      } else {
        tracking()?.track(EventTypes.IS_NOT_CHEAPER, {
          supplier: currentDeal?.provider.name,
          deal: currentDeal?.name,
          difference_dkk_pr_year: difference
            ? parseInt(difference.toString())
            : 0,
          current_dkk_pr_year: totalCurrent ? addVat(totalCurrent) : 0,
          compare_dkk_pr_year: addVat(totalCompare),
        });
      }
    }

    // Update the ref with the current state
    previousIsCheaperRef.current = isCurrentlyCheaper;
  }, [totalCurrent, totalCompare, currentDeal, difference, addVat]);
};

export default useTracking;
